import React, { useEffect, useRef, useState } from 'react';
import '../styles/PageLoader.css';
// Le wordmark sert de pochoir (mask CSS) : seule sa forme compte, l'alpha du
// PNG découpe les lettres, la couleur vient du CSS. Un seul fichier suffit
// pour les deux thèmes.
import motSpot from '../LogoSpot/spot-nom-fond-noir.png';

/**
 * CHARGEMENT DE PAGE — UN SEUL LOADER GLOBAL
 *
 * Le visuel de chargement ne vit plus dans les pages : c'est un écran unique
 * (GlobalPageLoader, monté une fois dans le layout) qui recouvre la zone de
 * contenu tant qu'au moins un chargement est en cours.
 *
 * Les pages continuent d'utiliser <PageLoader text="…" /> comme avant, mais
 * ce composant ne rend plus rien : il « tient » simplement le loader global
 * ouvert tant qu'il est monté. Si plusieurs chargements s'enchaînent (code du
 * module via Suspense, puis données de la page), l'écran et sa jauge restent
 * LES MÊMES du début à la fin : un seul chargement perçu, une seule jauge qui
 * finit à 100 % puis s'efface en fondu.
 */

// ── Store module : chargements en cours + abonnés ───────────────────────────
let seq = 0;
const holds = new Map(); // id → label
const subs = new Set();
const notify = () => subs.forEach((fn) => fn());

/** Tient le loader global ouvert ; renvoie la fonction pour le relâcher. */
export function acquirePageLoad(label) {
    const id = ++seq;
    holds.set(id, label || 'Chargement…');
    notify();
    return () => {
        holds.delete(id);
        notify();
    };
}

/**
 * Composant utilisé par les pages : `if (loading) return <PageLoader text… />`.
 * Ne rend rien — il maintient le loader global ouvert tant qu'il est monté.
 * (La prop `inline` d'avant est acceptée mais ignorée : il n'y a plus qu'un
 * seul et même écran de chargement.)
 */
const PageLoader = ({ text = 'Chargement…' }) => {
    useEffect(() => acquirePageLoad(text), [text]);
    return null;
};

export default PageLoader;

// ── L'écran de chargement unique ────────────────────────────────────────────
// Monté une seule fois dans AppLayout, par-dessus la zone de contenu.
// Phases : hidden (rien) → loading (jauge qui avance) → finishing (jauge à
// 100 % + fondu de sortie) → hidden. Une courte fenêtre de 250 ms absorbe les
// passages de relais entre deux chargements pour ne jamais scintiller.
export const GlobalPageLoader = () => {
    const [phase, setPhase] = useState('hidden');
    const [label, setLabel] = useState('Chargement…');
    const [pct, setPct] = useState(6);
    const phaseRef = useRef('hidden');
    const showTimerRef = useRef(null);
    const finishTimersRef = useRef([]);

    useEffect(() => {
        const setPhaseSafe = (p) => { phaseRef.current = p; setPhase(p); };
        const clearShowTimer = () => {
            if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null; }
        };
        const clearFinishTimers = () => {
            finishTimersRef.current.forEach(clearTimeout);
            finishTimersRef.current = [];
        };

        const onChange = () => {
            if (holds.size > 0) {
                const labels = Array.from(holds.values());
                setLabel(labels[labels.length - 1]);
                if (phaseRef.current === 'hidden') {
                    // Grâce de 120 ms avant d'afficher le rideau : une page
                    // servie par le cache (~30 ms) ne le fait jamais apparaître.
                    if (!showTimerRef.current) {
                        showTimerRef.current = setTimeout(() => {
                            showTimerRef.current = null;
                            if (holds.size > 0) { setPct(8); setPhaseSafe('loading'); }
                        }, 120);
                    }
                } else {
                    // Relais entre deux chargements : le rideau reste tel quel.
                    clearFinishTimers();
                    setPhaseSafe('loading');
                }
            } else {
                clearShowTimer();
                if (phaseRef.current === 'loading') {
                    // Dernier chargement relâché. On attend 250 ms : si un autre
                    // prend le relais (module → données), rien ne bouge à l'écran.
                    clearFinishTimers();
                    finishTimersRef.current.push(setTimeout(() => {
                        if (phaseRef.current !== 'loading') return;
                        setPhaseSafe('finishing');
                        setPct(100);
                        finishTimersRef.current.push(setTimeout(() => setPhaseSafe('hidden'), 650));
                    }, 250));
                }
            }
        };

        subs.add(onChange);
        onChange();
        return () => { subs.delete(onChange); clearShowTimer(); clearFinishTimers(); };
    }, []);

    // Progression réaliste : rapide au début, ralentit, plafonne à 92 %.
    useEffect(() => {
        if (phase !== 'loading') return undefined;
        const iv = setInterval(() => {
            setPct((p) => Math.min(p + Math.max(0.5, (92 - p) * 0.085), 92));
        }, 110);
        return () => clearInterval(iv);
    }, [phase]);

    if (phase === 'hidden') return null;

    const masque = { WebkitMaskImage: `url(${motSpot})`, maskImage: `url(${motSpot})` };

    return (
        <div
            className={`ploader ploader-overlay${phase === 'finishing' ? ' ploader-overlay--finishing' : ''}`}
            role="status"
            aria-live="polite"
        >
            {/* Le wordmark SPOT EST la jauge : les lettres se remplissent du
                dégradé bleu → violet selon la vraie progression. */}
            <div className="ploader-marque" aria-hidden="true">
                <div className="ploader-mot ploader-mot--fond" style={masque} />
                <div
                    className="ploader-mot ploader-mot--rempli"
                    style={{ ...masque, clipPath: `inset(0 ${100 - pct}% 0 0)` }}
                />
            </div>
            <div className="ploader-text">{label}</div>
            <div className="ploader-pct">{Math.round(pct)}&nbsp;%</div>
        </div>
    );
};
